import React, { useState, useEffect } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { getInsurance, getCountryConfig, calculate, exportPDF, exportExcel, getClientSuggestions } from '../services/api';
import toast from 'react-hot-toast';

const COUNTRIES = ['Saudi Arabia', 'India', 'Australia', 'United States', 'Netherlands', 'Japan', 'Norway', 'China'];
const FREQUENCIES = ['Per Hour','Per Day','Per Week','Per Month','Per Year'];
const AUS_STATES  = ['New South Wales','Victoria','Australian Capital Territory','Queensland','South Australia','Northern Territory','Western Australia'];
const VISA_TYPES  = ['None','AP Iqama','China Harbour Iqama','Work Permit'];
const CHINA_CITIES = ['Guangzhou','Dajin','Qinzhou','Zhongshan','Qingdao','Hefei','Shanghai','Nantong','Yantai','Beijing','Xian','Tianjin'];
const NORWAY_SS_ZONES = ['Zone 1','Zone 2','Zone 3','Zone 4','Zone 5','Expat from USA/Canada'];
const NORWAY_TAX_METHODS = ['Top Rate','Actual Tax'];
const NL_CONTRACT_TYPES = ['Temporary','Indefinite'];
const US_STATES = ['AK','AL','AR','AZ','CA','CO','CT','DC','DE','FL','GA','HI','IA','ID','IL','IN','KS','KY','LA','MA','MD','ME','MI','MN','MO','MS','MT','NC','ND','NH','NJ','NM','NV','NY','OH','OK','OR','PA','SC','SD','TN','TX','USVI','UT','VA','VT','WA','WI','WV','WY'];

const fmt = (v, d=2) => v == null ? '—' : parseFloat(v).toLocaleString('en-US',{minimumFractionDigits:d,maximumFractionDigits:d});
const fmtP = v => v == null ? '' : (parseFloat(v)*100).toFixed(2)+'%';

function ClientAutocomplete({ value, onChange }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen]               = useState(false);
  const [focused, setFocused]         = useState(false);
  const debounceRef = React.useRef(null);

  const fetchSuggestions = (q) => {
    clearTimeout(debounceRef.current);
    if (!q.trim()) { setSuggestions([]); setOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await getClientSuggestions(q);
        setSuggestions(data);
        setOpen(data.length > 0);
      } catch { setSuggestions([]); setOpen(false); }
    }, 250);
  };

  const handleChange = e => {
    onChange(e.target.value);
    fetchSuggestions(e.target.value);
  };

  const handleSelect = name => {
    onChange(name);
    setSuggestions([]);
    setOpen(false);
  };

  const handleBlur = () => {
    // Small delay so click on suggestion registers before blur closes dropdown
    setTimeout(() => { setOpen(false); setFocused(false); }, 150);
  };

  return (
    <div style={{ position:'relative' }}>
      <input
        className="form-control"
        type="text"
        placeholder="Enter client name"
        value={value}
        onChange={handleChange}
        onFocus={() => setFocused(true)}
        onBlur={handleBlur}
        autoComplete="off"
      />
      {open && (
        <div style={{
          position:'absolute', top:'100%', left:0, right:0, zIndex:200,
          background:'var(--surface)', border:'1.5px solid var(--accent)',
          borderTop:'none', borderRadius:'0 0 var(--r-sm) var(--r-sm)',
          boxShadow:'0 4px 12px rgba(0,0,0,.12)', overflow:'hidden',
        }}>
          {suggestions.map(name => (
            <div key={name} onMouseDown={() => handleSelect(name)}
              style={{
                padding:'9px 13px', fontSize:13.5, cursor:'pointer',
                color:'var(--text)', borderBottom:'1px solid var(--border)',
                display:'flex', alignItems:'center', gap:8,
              }}
              onMouseEnter={e => e.currentTarget.style.background='var(--surface2)'}
              onMouseLeave={e => e.currentTarget.style.background='transparent'}>
              <span style={{ color:'var(--muted)', fontSize:12 }}>🏢</span>
              {name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BRow({ label, rate, value, currency, isSubtotal, isTotal, indent }) {
  const bg    = isTotal ? '#1e3a5f' : isSubtotal ? '#dbeafe' : 'transparent';
  const color = isTotal ? '#fff' : '#1a2b3c';
  const fw    = isTotal || isSubtotal ? 600 : 400;
  const fs    = isTotal ? 14 : isSubtotal ? 13.5 : 13;
  const negColor = !isTotal && !isSubtotal && value < 0 ? '#ef4444' : color;
  return (
    <tr style={{ background: bg }}>
      <td style={{ padding:'8px 16px', paddingLeft: indent ? 30 : 16, fontSize:fs, fontWeight:fw, color, width:'54%' }}>{label}</td>
      <td style={{ padding:'8px 16px', fontSize:12, color: isTotal ? 'rgba(255,255,255,.75)' : '#64748b', textAlign:'right', width:'18%' }}>{rate != null ? fmtP(rate) : ''}</td>
      <td style={{ padding:'8px 16px', fontSize:fs, fontWeight:fw, color: negColor, textAlign:'right' }}>
        {value != null ? `${currency} ${fmt(value)}` : ''}
      </td>
    </tr>
  );
}

export default function CalculatorPage() {
  const [sp] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [blockModal, setBlockModal] = useState(null); // { type, message, code, quota_used, quota_limit, credit_balance }

  const [country, setCountry]   = useState(sp.get('country') || 'Saudi Arabia');
  const [ins,     setIns]       = useState([]);
  const [medInsurancePct, setMedInsurancePct] = useState(null);
  const [vmsFeePct,       setVmsFeePct]       = useState(null);
  const [result,  setResult]    = useState(null);
  const [loading, setLoading]   = useState(false);
  const [exporting, setExporting] = useState(null);

  const [f, setF] = useState({
    clientName:'',
    billRate:'', payFrequency:'Per Day', billingFrequency:'Per Day',
    rateType:'gross',
    inputCurrency:'USD', outputCurrency:'USD',
    contractorType: (sp.get('country') || 'Saudi Arabia') === 'Norway' ? 'Local' : 'Expat',
    medicalInsuranceName:'',
    includeEOSB:true, includeMOL:false, includeVisa:false,
    visaType:'None', includeMob:false, invoiceFromKSA:true,
    inApplyGst:false,
    inTaxMethod:'Top Rate',
    stateTerritory:'Western Australia',
    auApplyGst:false,
    auTaxMethod:'Top Rate',
    usState:'AL',
    usVmsEnabled:false,
    usTaxMethod:'Top Rate',
    usSutaOffsetsFuta:'Yes',
    chinaCity:'Beijing',
    cnApplyVat:false,
    cnTaxMethod:'Top Rate',
    noSsZone:'Zone 1',
    noTaxMethod:'Top Rate',
    noApplyVat:false,
    jpApplyConsumptionTax:false,
    jpTaxMethod:'Top Rate',
    nlContractType:'Temporary',
    nlApplyVat:false,
    nlTaxMethod:'Top Rate',
    nl30PctRuling:true,
    nlBurdensCapped:true,
    nlIncludeWaitingDay:false,
    nlTravelAllowance:false,
    nlTravelRatePerKm:0,
    nlKmPerDay:0,
  });

  const set = (k,v) => setF(p => ({...p,[k]:v}));

  const prefillToasted = React.useRef(false);

  // Load insurance options once
  useEffect(() => {
    getInsurance().then(r => setIns(r.data)).catch(()=>{});
  },[]);

  // Single effect for country changes — fetches config once, sets both pct and default insurance
  useEffect(() => {
    const isPrefilling = !!location.state?.prefill;
    if (!isPrefilling) setResult(null);
    getCountryConfig(country).then(({data}) => {
      const cfg = {};
      data.forEach(d => { cfg[d.param_key] = d.param_value; });
      setMedInsurancePct(cfg.medical_insurance_pct ? parseFloat(cfg.medical_insurance_pct) : null);
      setVmsFeePct(cfg.vms_fee_pct != null ? parseFloat(cfg.vms_fee_pct) : null);
      // Only set default insurance if not currently prefilling from saved quote
      if (!isPrefilling) {
        setF(p => ({...p, medicalInsuranceName: cfg.default_medical_insurance || ''}));
      }
    }).catch(()=>{});
  },[country]);

  // Prefill from saved quote revisit — runs once per navigation
  useEffect(() => {
    const prefill = location.state?.prefill;
    if (!prefill) return;
    const inp = prefill.inputs || {};
    setCountry(prefill.country || 'Saudi Arabia');
    setF(p => ({
      ...p,
      clientName:           prefill.client_name || '',
      billRate:             inp.billRate || '',
      payFrequency:         inp.payFrequency || 'Per Day',
      billingFrequency:     inp.billingFrequency || 'Per Day',
      rateType:             inp.rateType || 'gross',
      inputCurrency:        inp.inputCurrency || 'USD',
      outputCurrency:       inp.outputCurrency || 'USD',
      contractorType:       inp.contractorType || (inp.country === 'Norway' ? 'Local' : 'Expat'),
      medicalInsuranceName: inp.medicalInsuranceName || '',
      includeEOSB:          inp.includeEOSB ?? true,
      includeMOL:           inp.includeMOL ?? false,
      includeVisa:          inp.includeVisa ?? false,
      visaType:             inp.visaType || 'None',
      includeMob:           inp.includeMob ?? false,
      invoiceFromKSA:       inp.invoiceFromKSA ?? true,
      inApplyGst:           inp.inApplyGst         ?? false,
      inTaxMethod:          inp.inTaxMethod        || 'Top Rate',
      stateTerritory:       inp.stateTerritory || 'Western Australia',
      auApplyGst:           inp.auApplyGst         ?? false,
      auTaxMethod:          inp.auTaxMethod        || 'Top Rate',
      usState:              inp.usState           || 'AL',
      usVmsEnabled:         inp.usVmsEnabled      ?? false,
      usTaxMethod:          inp.usTaxMethod        || 'Top Rate',
      usSutaOffsetsFuta:    inp.usSutaOffsetsFuta  || 'Yes',
      chinaCity:            inp.chinaCity         || 'Beijing',
      cnApplyVat:           inp.cnApplyVat         ?? false,
      cnTaxMethod:          inp.cnTaxMethod        || 'Top Rate',
      noSsZone:             inp.noSsZone          || 'Zone 1',
      noTaxMethod:          inp.noTaxMethod        || 'Top Rate',
      noApplyVat:           inp.noApplyVat         ?? false,
      jpApplyConsumptionTax: inp.jpApplyConsumptionTax ?? false,
      jpTaxMethod:          inp.jpTaxMethod        || 'Top Rate',
      nlContractType:       inp.nlContractType     || 'Temporary',
      nlApplyVat:           inp.nlApplyVat         ?? false,
      nlTaxMethod:          inp.nlTaxMethod        || 'Top Rate',
      nl30PctRuling:        inp.nl30PctRuling      ?? true,
      nlBurdensCapped:      inp.nlBurdensCapped     ?? true,
      nlIncludeWaitingDay:  inp.nlIncludeWaitingDay ?? false,
      nlTravelAllowance:    inp.nlTravelAllowance   ?? false,
      nlTravelRatePerKm:    inp.nlTravelRatePerKm   || 0,
      nlKmPerDay:           inp.nlKmPerDay          || 0,
    }));
    // Load the saved result directly — no need to recalculate
    if (prefill.result) {
      setResult(prefill.result);
    }
    if (!prefillToasted.current) {
      prefillToasted.current = true;
      toast.success(`Loaded: ${prefill.client_name}`);
    }
  }, [location.state]);

  const handleCalc = async () => {
    if (!f.billRate) return toast.error('Enter a pay rate');
    setLoading(true);
    try {
      const { data } = await calculate({
        country,
        inputs: {
          billRate: parseFloat(f.billRate), payFrequency: f.payFrequency,
          billingFrequency: f.billingFrequency,
          rateType: f.rateType,
          inputCurrency: f.inputCurrency, outputCurrency: f.outputCurrency,
          contractorType: f.contractorType,
          medicalInsuranceName: f.medicalInsuranceName,
          includeEOSB: f.includeEOSB, includeMOL: f.includeMOL,
          includeVisa: f.includeVisa, visaType: f.visaType,
          includeMob: f.includeMob, invoiceFromKSA: f.invoiceFromKSA,
          inApplyGst: !!f.inApplyGst,
          inTaxMethod: f.inTaxMethod,
          stateTerritory: f.stateTerritory,
          auApplyGst: !!f.auApplyGst,
          auTaxMethod: f.auTaxMethod,
          usState: f.usState,
          usVmsEnabled: !!f.usVmsEnabled,
          usTaxMethod: f.usTaxMethod,
          usSutaOffsetsFuta: f.usSutaOffsetsFuta,
          chinaCity: f.chinaCity,
          cnApplyVat: !!f.cnApplyVat,
          cnTaxMethod: f.cnTaxMethod,
          noSsZone: f.noSsZone,
          noTaxMethod: f.noTaxMethod,
          noApplyVat: f.noApplyVat,
          jpApplyConsumptionTax: !!f.jpApplyConsumptionTax,
          jpTaxMethod: f.jpTaxMethod,
          nlContractType: f.nlContractType,
          nlApplyVat: f.nlApplyVat,
          nlTaxMethod: f.nlTaxMethod,
          nl30PctRuling: !!f.nl30PctRuling,
          nlBurdensCapped: f.nlBurdensCapped,
          nlIncludeWaitingDay: f.nlIncludeWaitingDay,
          nlTravelAllowance: f.nlTravelAllowance,
          nlTravelRatePerKm: parseFloat(f.nlTravelRatePerKm) || 0,
          nlKmPerDay: parseFloat(f.nlKmPerDay) || 0,
          clientName: f.clientName,
        },
      });
      setResult(data);
      toast.success('Calculation complete!');
    } catch(err) {
      const data = err.response?.data;
      const code = data?.code;
      if (code === 'QUOTA_EXCEEDED') {
        setBlockModal({
          type: 'quota',
          title: 'Monthly quota reached',
          message: data.message,
          quota_used: data.quota_used,
          quota_limit: data.quota_limit,
          credit_balance: data.credit_balance,
        });
      } else if (code === 'FEATURE_NOT_AVAILABLE') {
        setBlockModal({
          type: 'feature',
          title: 'Feature not available on your plan',
          message: data.message,
          current_plan: data.current_plan,
        });
      } else if (code === 'COUNTRY_NOT_ALLOWED') {
        setBlockModal({
          type: 'country',
          title: 'Country not included in your plan',
          message: data.message,
          allowed_countries: data.allowed_countries,
        });
      } else {
        toast.error(data?.message || 'Calculation failed');
      }
    }
    finally { setLoading(false); }
  };

  const doExport = async type => {
    if (!result) return;
    setExporting(type);
    try {
      const body = {
        country,
        inputs: {
          billRate:parseFloat(f.billRate), payFrequency:f.payFrequency,
          billingFrequency:f.billingFrequency,
          rateType:f.rateType,
          inputCurrency:f.inputCurrency, outputCurrency:f.outputCurrency,
          contractorType:f.contractorType,
          medicalInsuranceName:f.medicalInsuranceName,
          includeEOSB:f.includeEOSB, includeMOL:f.includeMOL,
          includeVisa:f.includeVisa, visaType:f.visaType,
          includeMob:f.includeMob, invoiceFromKSA:f.invoiceFromKSA,
          stateTerritory:f.stateTerritory,
        },
        clientName: f.clientName,
      };
      if (type==='pdf') await exportPDF(body); else await exportExcel(body);
      toast.success(`${type.toUpperCase()} downloaded`);
    } catch { toast.error('Export failed'); }
    finally { setExporting(null); }
  };

  const bd = result?.breakdown;
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '360px 1fr', gap:22, alignItems:'stretch' }}>
      {/* ── LEFT PANEL ── */}
      <div style={{ display:'flex', flexDirection:'column', position: isMobile ? 'static' : 'sticky', top:0, maxHeight: isMobile ? 'none' : 'calc(100vh - 72px)' }}>

        {/* Title — sticky, never scrolls */}
        <div style={{ paddingLeft:2, paddingBottom:14, flexShrink:0 }}>
          <h1 className="page-title" style={{ marginBottom:2 }}>🧮 Cost Calculator</h1>
          <p className="page-sub" style={{ marginBottom:0 }}>Calculate contractor placement costs</p>
        </div>

        {/* Scrollable form — only scrollable on desktop */}
        <div style={{ display:'flex', flexDirection:'column', gap:16, overflowY: isMobile ? 'visible' : 'auto', paddingRight: isMobile ? 0 : 4, paddingBottom:24, flex: isMobile ? 'none' : 1, minHeight:0 }}>
        <div className="card">
            <div className="card-header"><span style={{ fontWeight:600, color:'var(--text)', fontSize:13.5 }}>📋 Contract Details</span></div>
            <div className="card-body">
              <div className="form-group">
                <label className="form-label">Country</label>
                <select className="form-control" value={country} onChange={e => { setCountry(e.target.value); if(e.target.value==='Norway') set('contractorType','Local'); else if(country==='Norway') set('contractorType','Expat'); }}>
                  {COUNTRIES.map(c => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Client</label>
                <ClientAutocomplete value={f.clientName} onChange={v => set('clientName', v)} />
              </div>
              <div className="grid2">
                <div className="form-group">
                  <label className="form-label">Pay Rate</label>
                  <input className="form-control" type="number" placeholder="0.00" value={f.billRate} onChange={e => set('billRate',e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Pay Frequency</label>
                  <select className="form-control" value={f.payFrequency} onChange={e => set('payFrequency',e.target.value)}>
                    {FREQUENCIES.map(x => <option key={x}>{x}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Bill Frequency</label>
                <select className="form-control" value={f.billingFrequency} onChange={e => set('billingFrequency',e.target.value)}>
                  {FREQUENCIES.map(x => <option key={x}>{x}</option>)}
                </select>
                <div style={{ fontSize:11.5, color:'var(--muted)', marginTop:4 }}>
                  Output breakdown will be shown in this unit — e.g. Per Month shows all figures as monthly amounts
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Rate Type</label>
                <div style={{ display:'flex', gap:8 }}>
                  {[['gross','Gross Rate'],['net','Net Rate']].map(([val,lbl]) => (
                    <button key={val} type="button" onClick={() => set('rateType', val)}
                      className="btn"
                      style={{ flex:1, justifyContent:'center', padding:'8px 12px',
                        background: f.rateType === val ? 'var(--primary)' : 'var(--surface2)',
                        color:      f.rateType === val ? '#fff' : 'var(--text)',
                        border:     f.rateType === val ? 'none' : '1.5px solid var(--border)',
                        fontWeight: f.rateType === val ? 600 : 400,
                      }}>
                      {lbl}
                    </button>
                  ))}
                </div>
                <div style={{ fontSize:11.5, color:'var(--muted)', marginTop:4 }}>
                  {f.rateType === 'net'
                    ? 'Enter what the contractor takes home — invoice rate will be grossed up'
                    : 'Enter the gross bill rate charged to the client'}
                </div>
              </div>
              <div className="grid2">
                <div className="form-group">
                  <label className="form-label">Contractor Type</label>
                  <select className="form-control" value={f.contractorType}
                    onChange={e => set('contractorType',e.target.value)}
                    disabled={country==='Norway'}
                    style={country==='Norway' ? { opacity:0.6, cursor:'not-allowed' } : {}}>
                    <option>Expat</option>
                    <option>Local</option>
                    {country==='Saudi Arabia' && <option>Expat Short Term</option>}
                  </select>
                  {country==='Norway' && (
                    <div style={{ fontSize:11.5, color:'var(--muted)', marginTop:4 }}>
                      Only Local is supported for Norway
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">Input Currency</label>
                  <select className="form-control" value={f.inputCurrency} onChange={e => set('inputCurrency',e.target.value)}>
                    {['USD','GBP','EUR','SAR','INR','AUD','AED','QAR','JPY','CNY','NOK'].map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Output Currency</label>
                <select className="form-control" value={f.outputCurrency} onChange={e => set('outputCurrency',e.target.value)}>
                  {['USD','GBP','EUR','SAR','INR','AUD','AED','QAR','JPY','CNY','NOK'].map(c => <option key={c}>{c}</option>)}
                </select>
                <div style={{ fontSize:11.5, color:'var(--muted)', marginTop:4 }}>
                  All output figures will be shown in this currency
                </div>
              </div>
            </div>
        </div>

          {/* Country-specific settings */}
        <div className="card">
            <div className="card-header">
              <span style={{ fontWeight:600, color:'var(--text)', fontSize:13.5 }}>
                {country} Settings
              </span>
            </div>
            <div className="card-body">
              <div className="form-group">
                <label className="form-label">Medical Insurance</label>
                <select className="form-control" value={f.medicalInsuranceName} onChange={e => set('medicalInsuranceName',e.target.value)}>
                  {country === 'Japan' && <option value="">— Select insurance plan —</option>}
                  <option value="None">None</option>
                  <option value="Percentage">Percentage of Rate</option>
                  {ins.map(p => <option key={p.id} value={p.name}>{p.name} ({p.currency_code} {fmt(p.amount)} /{p.frequency.replace('Per ','')})</option>)}
                </select>
                {f.medicalInsuranceName === 'Percentage' && (
                  <div style={{ marginTop:7, padding:'7px 10px', background:'var(--surface2)', borderRadius:'var(--r)', border:'1px solid var(--border)', fontSize:13, color:'var(--text2)', display:'flex', alignItems:'center', gap:6 }}>
                    <span style={{ color:'var(--muted)', fontSize:12 }}>Applied rate:</span>
                    <span style={{ fontWeight:600, color:'var(--text)' }}>
                      {medInsurancePct != null ? `${(medInsurancePct * 100).toFixed(1)}% of Gross Rate` : '—'}
                    </span>
                  </div>
                )}
                {country === 'Japan' && (
                  <div style={{ marginTop:7, padding:'7px 10px', background:'#fefce8', borderRadius:'var(--r)', border:'1px solid #fef08a', fontSize:12, color:'#854d0e', display:'flex', alignItems:'center', gap:6 }}>
                    <span>⚠</span>
                    <span>Japan applies <strong>0% medical insurance</strong> by default.</span>
                  </div>
                )}
              </div>

              {country==='Saudi Arabia' && <>
                <div className="form-group">
                  <label className="form-label">Invoice from Quoteology KSA?</label>
                  <select className="form-control" value={f.invoiceFromKSA?'yes':'no'} onChange={e => set('invoiceFromKSA',e.target.value==='yes')}>
                    <option value="yes">Yes — VAT 15%</option>
                    <option value="no">No — WHT 15%</option>
                  </select>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:12 }}>
                  {[['includeEOSB','EOSB'],['includeMOL','MOL'],['includeMob','Mob Costs'],['includeVisa','Visa']].map(([k,lbl]) => (
                    <label key={k} style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, cursor:'pointer' }}>
                      <input type="checkbox" checked={f[k]} onChange={e => set(k,e.target.checked)} style={{ accentColor:'var(--primary)', cursor:'pointer' }} />
                      {lbl}
                    </label>
                  ))}
                </div>
                {f.includeVisa && (
                  <div className="form-group">
                    <label className="form-label">Visa Type</label>
                    <select className="form-control" value={f.visaType} onChange={e => set('visaType',e.target.value)}>
                      {VISA_TYPES.map(v => <option key={v}>{v}</option>)}
                    </select>
                  </div>
                )}
              </>}

              {country==='India' && (<>
                <div className="form-group">
                  <label className="form-label">Tax Method</label>
                  <select className="form-control" value={f.inTaxMethod} onChange={e => set('inTaxMethod', e.target.value)}>
                    <option value="Top Rate">Top Rate</option>
                    <option value="Actual Tax">Actual Tax</option>
                  </select>
                </div>
                <div className="form-group">
                  <label style={{ display:'flex', alignItems:'center', gap:8, marginTop:6, cursor:'pointer' }}>
                    <input type="checkbox" checked={!!f.inApplyGst} onChange={e => set('inApplyGst', e.target.checked)} />
                    <span style={{ fontSize:13 }}>Apply GST (Goods and Services Tax)</span>
                  </label>
                  <div style={{ fontSize:11.5, color:'var(--muted)', marginTop:4 }}>Standard rate 18% — configurable via Country Config</div>
                </div>
              </>)}

              {country==='Australia' && (<>
                <div className="form-group">
                  <label className="form-label">Tax Method</label>
                  <select className="form-control" value={f.auTaxMethod} onChange={e => set('auTaxMethod', e.target.value)}>
                    <option value="Top Rate">Top Rate</option>
                    <option value="Actual Tax">Actual Tax</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">State / Territory</label>
                  <select className="form-control" value={f.stateTerritory} onChange={e => set('stateTerritory',e.target.value)}>
                    {AUS_STATES.map(s => <option key={s}>{s}</option>)}
                  </select>
                  <div style={{ fontSize:11.5, color:'var(--muted)', marginTop:4 }}>Determines payroll tax rate</div>
                </div>
                <div className="form-group">
                  <label style={{ display:'flex', alignItems:'center', gap:8, marginTop:6, cursor:'pointer' }}>
                    <input type="checkbox" checked={!!f.auApplyGst} onChange={e => set('auApplyGst', e.target.checked)} />
                    <span style={{ fontSize:13 }}>Apply GST (Goods and Services Tax)</span>
                  </label>
                  <div style={{ fontSize:11.5, color:'var(--muted)', marginTop:4 }}>Standard rate 10% — configurable via Country Config</div>
                </div>
              </>)}
              {country==='United States' && (<>
                <div className="form-group">
                  <label className="form-label">Tax Method</label>
                  <select className="form-control" value={f.usTaxMethod} onChange={e => set('usTaxMethod', e.target.value)}>
                    <option value="Top Rate">Top Rate</option>
                    <option value="Actual Tax">Actual Tax</option>
                  </select>
                  <div style={{ fontSize:11.5, color:'var(--muted)', marginTop:4 }}>Actual Tax calculation coming soon</div>
                </div>
                <div className="form-group">
                  <label className="form-label">State</label>
                  <select className="form-control" value={f.usState} onChange={e => set('usState',e.target.value)}>
                    {US_STATES.map(s => <option key={s}>{s}</option>)}
                  </select>
                  <div style={{ fontSize:11.5, color:'var(--muted)', marginTop:4 }}>Determines SUTA wage limit and rate</div>
                </div>
                <div className="form-group" style={{ marginBottom:0 }}>
                  <label className="form-label">VMS Fee</label>
                  <label style={{ display:'flex', alignItems:'center', gap:8, marginTop:6, cursor:'pointer' }}>
                    <input type="checkbox" checked={!!f.usVmsEnabled} onChange={e => set('usVmsEnabled', e.target.checked)} />
                    <span style={{ fontSize:13 }}>Apply VMS Fee</span>
                  </label>
                  <div style={{ fontSize:11.5, color:'var(--muted)', marginTop:4 }}>
                    Current VMS fee for United States is {vmsFeePct != null ? `${(vmsFeePct * 100).toFixed(2)}%` : '—'}
                  </div>
                </div>
                <div className="form-group" style={{ marginBottom:0 }}>
                  <label className="form-label">SUTA Offsets FUTA?</label>
                  <select className="form-control" value={f.usSutaOffsetsFuta} onChange={e => set('usSutaOffsetsFuta',e.target.value)}>
                    <option value="Yes">Yes — FUTA reduced by SUTA credit</option>
                    <option value="No">No — FUTA at full 6%</option>
                  </select>
                </div>
              </>)}

              {country==='Norway' && (<>
                <div className="form-group">
                  <label className="form-label">Social Security Zone</label>
                  <select className="form-control" value={f.noSsZone} onChange={e => set('noSsZone',e.target.value)}>
                    {NORWAY_SS_ZONES.map(s => <option key={s}>{s}</option>)}
                  </select>
                  <div style={{ fontSize:11.5, color:'var(--muted)', marginTop:4 }}>Zone 1=14.1%, Zone 2=10.6%, Zone 3=6.4%, Zone 4=5.1%, Zone 5=0%, Expat USA/CA=7%</div>
                </div>
                <div className="form-group" style={{ marginBottom:8 }}>
                  <label className="form-label">Tax Method</label>
                  <select className="form-control" value={f.noTaxMethod} onChange={e => set('noTaxMethod',e.target.value)}>
                    {NORWAY_TAX_METHODS.map(s => <option key={s}>{s}</option>)}
                  </select>
                  <div style={{ fontSize:11.5, color:'var(--muted)', marginTop:4 }}>Top Rate applies 15.4% flat; Actual Tax uses progressive slabs</div>
                </div>
                <div className="form-group" style={{ marginBottom:0, paddingBottom: f.noApplyVat ? 8 : 0 }}>
                  <label style={{ display:'flex', alignItems:'center', gap:8, marginTop:6, cursor:'pointer' }}>
                    <input type="checkbox" checked={!!f.noApplyVat} onChange={e => set('noApplyVat', e.target.checked)} />
                    <span style={{ fontSize:13 }}>Apply MVA (VAT)</span>
                  </label>
                  {f.noApplyVat && (
                    <div style={{ marginTop:6, marginBottom:8, padding:'6px 10px', background:'#fefce8', borderRadius:'var(--r)', border:'1px solid #fef08a', fontSize:12, color:'#854d0e' }}>
                      25% MVA (VAT) will be applied to the invoice value.
                    </div>
                  )}
                </div>
              </>)}
              {country==='Netherlands' && (<>

                <div className="form-group">
                  <label className="form-label">Tax Method</label>
                  <select className="form-control" value={f.nlTaxMethod} onChange={e => set('nlTaxMethod', e.target.value)}>
                    <option value="Top Rate">Top Rate</option>
                    <option value="Actual Tax">Actual Tax</option>
                  </select>
                </div>
                {f.nlTaxMethod === 'Actual Tax' && f.contractorType === 'Expat' && (
                  <div className="form-group">
                    <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer' }}>
                      <input type="checkbox" checked={!!f.nl30PctRuling} onChange={e => set('nl30PctRuling', e.target.checked)} />
                      <span style={{ fontSize:13 }}>Apply 30% Ruling</span>
                    </label>
                    <div style={{ fontSize:11.5, color:'var(--muted)', marginTop:4 }}>30% of gross salary is tax-free for qualifying expats</div>
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label" style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <input type="checkbox" checked={!!f.nlBurdensCapped} onChange={e => set('nlBurdensCapped', e.target.checked)} />
                    <span>Employer Burdens Capped (R76)</span>
                  </label>
                  <div style={{ fontSize:11.5, color:'var(--muted)', marginTop:4 }}>Applies annual €66,952 cap to WAO-WIA, WAO-ZW, ZVW, Unemployment</div>
                </div>
                <div className="form-group">
                  <label className="form-label" style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <input type="checkbox" checked={!!f.nlTravelAllowance} onChange={e => set('nlTravelAllowance', e.target.checked)} />
                    <span>Travel Allowance (R69)</span>
                  </label>
                </div>
                {f.nlTravelAllowance && (<>
                  <div className="form-group">
                    <label className="form-label">Rate per km (output currency)</label>
                    <input type="number" className="form-control" value={f.nlTravelRatePerKm} onChange={e => set('nlTravelRatePerKm', e.target.value)} step="0.01" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">km per day</label>
                    <input type="number" className="form-control" value={f.nlKmPerDay} onChange={e => set('nlKmPerDay', e.target.value)} step="0.1" />
                  </div>
                </>)}
                <div className="form-group" style={{ marginBottom:0 }}>
                  <label style={{ display:'flex', alignItems:'center', gap:8, marginTop:6, cursor:'pointer' }}>
                    <input type="checkbox" checked={!!f.nlApplyVat} onChange={e => set('nlApplyVat', e.target.checked)} />
                    <span style={{ fontSize:13 }}>Apply BTW (VAT)</span>
                  </label>
                  {f.nlApplyVat && (
                    <div style={{ marginTop:6, marginBottom:8, padding:'6px 10px', background:'#fefce8', borderRadius:'var(--r)', border:'1px solid #fef08a', fontSize:12, color:'#854d0e' }}>
                      21% BTW (VAT) will be applied to the invoice value.
                    </div>
                  )}
                </div>
              </>)}
              {country==='Japan' && (<>
                <div className="form-group">
                  <label className="form-label">Tax Method</label>
                  <select className="form-control" value={f.jpTaxMethod} onChange={e => set('jpTaxMethod', e.target.value)}>
                    <option value="Top Rate">Top Rate</option>
                    <option value="Actual Tax">Actual Tax</option>
                  </select>
                </div>
                <div className="form-group">
                  <label style={{ display:'flex', alignItems:'center', gap:8, marginTop:6, cursor:'pointer' }}>
                    <input type="checkbox" checked={!!f.jpApplyConsumptionTax} onChange={e => set('jpApplyConsumptionTax', e.target.checked)} />
                    <span style={{ fontSize:13 }}>Apply JCT (Consumption Tax)</span>
                  </label>
                  <div style={{ fontSize:11.5, color:'var(--muted)', marginTop:4 }}>Standard rate 10% since Oct 2019 — configurable via Country Config</div>
                </div>
              </>)}

              {country==='China' && (<>
                <div className="form-group">
                  <label className="form-label">Tax Method</label>
                  <select className="form-control" value={f.cnTaxMethod} onChange={e => set('cnTaxMethod', e.target.value)}>
                    <option value="Top Rate">Top Rate</option>
                    <option value="Actual Tax">Actual Tax</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">City / Region</label>
                  <select className="form-control" value={f.chinaCity} onChange={e => set('chinaCity',e.target.value)}>
                    {CHINA_CITIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                  <div style={{ fontSize:11.5, color:'var(--muted)', marginTop:4 }}>Determines social security base amounts</div>
                </div>
                <div className="form-group">
                  <label style={{ display:'flex', alignItems:'center', gap:8, marginTop:6, cursor:'pointer' }}>
                    <input type="checkbox" checked={!!f.cnApplyVat} onChange={e => set('cnApplyVat', e.target.checked)} />
                    <span style={{ fontSize:13 }}>Apply VAT</span>
                  </label>
                  <div style={{ fontSize:11.5, color:'var(--muted)', marginTop:4 }}>Standard rate 13% since April 2019 — configurable via Country Config</div>
                </div>
              </>)}
            </div>
        </div>

        </div>{/* end scrollable form */}

        {/* Calculate button — always visible at bottom */}
        <div style={{ paddingTop:12, flexShrink:0 }}>
          <button className="btn btn-primary btn-lg" onClick={handleCalc} disabled={loading}
            style={{ justifyContent:'center', width:'100%' }}>
            {loading ? <><div className="spinner"/>Calculating...</> : '⚡ Calculate Cost'}
          </button>
        </div>

      </div>{/* end left panel */}

        {/* ── RIGHT PANEL ── */}
        <div style={{ overflowY:'auto', minHeight:0 }}>
          {!result ? (
            <div className="card" style={{ textAlign:'center', padding:'80px 40px', height:'100%', boxSizing:'border-box', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
              <div style={{ fontSize:56, marginBottom:16 }}>🧮</div>
              <div style={{ fontSize:17, fontWeight:600, color:'var(--text)', marginBottom:8 }}>Ready to Calculate</div>
              <div style={{ fontSize:13.5, color:'var(--text2)' }}>Fill in the form and click Calculate Cost</div>
            </div>
          ) : <>
            {/* Country banner */}
            <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:14, padding:'12px 18px', background:'var(--primary)', borderRadius:'var(--r)', borderLeft:'3px solid var(--accent)' }}>
              <div>
                <div style={{ fontWeight:700, fontSize:15, color:'#fff', letterSpacing:'-.2px' }}>{result.country}</div>
                <div style={{ fontSize:12, color:'rgba(255,255,255,.55)', marginTop:3, display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                  <span style={{ background:'rgba(14,165,233,.2)', color:'#38BDF8', padding:'1px 8px', borderRadius:20, fontSize:11, fontWeight:500 }}>{result.contractorType}</span>
                  <span style={{ color:'rgba(255,255,255,.4)' }}>·</span>
                  <span>{result.inputCurrency} → {result.currency}</span>
                  <span style={{ color:'rgba(255,255,255,.4)' }}>·</span>
                  <span>{result.payFrequency} → {result.billingFrequency}</span>
                </div>
              </div>
            </div>

            {/* Summary row */}
            <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap:14, marginBottom:18 }}>
              <div className="card" style={{ textAlign:'center', padding:'18px' }}>
                <div style={{ fontSize:12, color:'var(--text2)', marginBottom:4 }}>
                  {result.rateType === 'net' ? 'Gross Rate' : 'Bill Rate'} / {(result.billingFrequency||'Per Day').replace('Per ','')}
                </div>
                <div style={{ fontSize:18, fontWeight:700, color:'var(--text)' }}>{result.currency} {fmt(result.billRateDisplay ?? result.billRatePerDay)}</div>
                {result.rateType === 'net' && (
                  <div style={{ fontSize:11, color:'var(--muted)', marginTop:2 }}>grossed up from net</div>
                )}
              </div>
              <div style={{ background:'var(--primary)', borderRadius:'var(--r)', textAlign:'center', padding:'18px', borderLeft:'3px solid var(--accent)' }}>
                <div style={{ fontSize:12, color:'rgba(255,255,255,.55)', marginBottom:4 }}>Invoice / {(result.billingFrequency||'Per Day').replace('Per ','')} (Post Tax)</div>
                <div style={{ fontSize:18, fontWeight:700, color:'#fff' }}>{result.currency} {fmt(bd.invoicePostTax.value)}</div>
              </div>
              <div className="card" style={{ textAlign:'center', padding:'18px' }}>
                <div style={{ fontSize:12, color:'var(--text2)', marginBottom:4 }}>Multiplier</div>
                <div style={{ fontSize:20, fontWeight:700, color:'var(--precision)' }}>{result.multiplierPostTax}x</div>
                <div style={{ fontSize:11, color:'var(--muted)' }}>Pre-tax: {result.multiplierPreTax}x</div>
              </div>
            </div>

            {/* Breakdown table */}
            <div className="card">
              <div className="card-header">
                <div>
                  <div style={{ fontWeight:700, color:'var(--text)', fontSize:14 }}>
                    Quoteology Billing Breakdown — {result.billingFrequency || result.payFrequency} Basis
                  </div>
                  <div style={{ fontSize:11.5, color:'var(--text2)', marginTop:3 }}>
                    {result.country} · {result.contractorType} · {result.currency}
                    {result.stateTerritory ? ` · ${result.stateTerritory}` : ''}
                    {' · Contractor paid '}{result.payFrequency}
                  </div>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <button className="btn btn-sm btn-outline" onClick={() => doExport('pdf')} disabled={!!exporting}>
                    {exporting==='pdf' ? <><div className="spinner" style={{borderTopColor:'var(--text2)'}}/></> : <><i className="ti ti-file-type-pdf" aria-hidden="true"/>PDF</>}
                  </button>
                  <button className="btn btn-sm btn-outline" onClick={() => doExport('excel')} disabled={!!exporting}>
                    {exporting==='excel' ? <><div className="spinner" style={{borderTopColor:'var(--text2)'}}/></> : <><i className="ti ti-file-spreadsheet" aria-hidden="true"/>Excel</>}
                  </button>
                </div>
              </div>

              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead>
                    <tr style={{ background:'#1e3a5f' }}>
                      <th style={{ padding:'10px 16px', textAlign:'left', color:'#fff', fontSize:12, fontWeight:600 }}>Description</th>
                      <th style={{ padding:'10px 16px', textAlign:'right', color:'rgba(255,255,255,.75)', fontSize:12, fontWeight:600, width:120 }}>Rate %</th>
                      <th style={{ padding:'10px 16px', textAlign:'right', color:'rgba(255,255,255,.75)', fontSize:12, fontWeight:600, width:180 }}>Amount/{(result.billingFrequency||'Per Day').replace('Per ','')} ({result.currency})</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bd.netRateToMan && <BRow label="Net Rate to Man" rate={null} value={bd.netRateToMan.value} currency={result.currency} />}
                    {/* Bug 3 fix: topRateTax (Australia 45%) renders before ssEmployee (2%) — matches Excel order */}
                    {bd.topRateTax  && <BRow label={bd.topRateTax.label}  rate={bd.topRateTax.rate}  value={bd.topRateTax.value}  currency={result.currency} indent />}
                    {bd.nlGeneralCredit && <BRow label={bd.nlGeneralCredit.label} rate={bd.nlGeneralCredit.rate} value={bd.nlGeneralCredit.value} currency={result.currency} indent />}
                    {bd.nlLabourCredit  && <BRow label={bd.nlLabourCredit.label}  rate={bd.nlLabourCredit.rate}  value={bd.nlLabourCredit.value}  currency={result.currency} indent />}
                    {bd.noMinstefradrag && <BRow label={bd.noMinstefradrag.label} rate={bd.noMinstefradrag.rate} value={bd.noMinstefradrag.value} currency={result.currency} indent />}
                    {bd.noPersonfradrag && <BRow label={bd.noPersonfradrag.label} rate={null}                    value={bd.noPersonfradrag.value} currency={result.currency} indent />}
                    {bd.noTrinnskatt   && <BRow label={bd.noTrinnskatt.label}   rate={bd.noTrinnskatt.rate}   value={bd.noTrinnskatt.value}   currency={result.currency} indent />}
                    {bd.jpEmpDeduction   && <BRow label={bd.jpEmpDeduction.label}   rate={null} value={bd.jpEmpDeduction.value}   currency={result.currency} indent />}
                    {bd.jpBasicDeduction && <BRow label={bd.jpBasicDeduction.label} rate={null} value={bd.jpBasicDeduction.value} currency={result.currency} indent />}
                    {bd.jpSsDeduction    && <BRow label={bd.jpSsDeduction.label}    rate={null} value={bd.jpSsDeduction.value}    currency={result.currency} indent />}
                    {bd.residentTax      && <BRow label={bd.residentTax.label}      rate={bd.residentTax.rate} value={bd.residentTax.value} currency={result.currency} indent />}
                    {bd.cnEmpSS          && <BRow label={bd.cnEmpSS.label}          rate={bd.cnEmpSS.rate} value={bd.cnEmpSS.value} currency={result.currency} indent />}
                    {bd.ssEmployee?.value!==0 && bd.ssEmployee && <BRow label={bd.ssEmployee.label} rate={bd.ssEmployee.rate} value={bd.ssEmployee.value} currency={result.currency} indent />}
                    {bd.pitTopRate  && <BRow label={bd.pitTopRate.label}  rate={bd.pitTopRate.rate}  value={bd.pitTopRate.value}  currency={result.currency} indent />}
                    {bd.inStandardDeduction && <BRow label={bd.inStandardDeduction.label} rate={null} value={bd.inStandardDeduction.value} currency={result.currency} indent />}
                    {bd.surcharge   && <BRow label={bd.surcharge.label}   rate={bd.surcharge.rate}   value={bd.surcharge.value}   currency={result.currency} indent />}
                    {bd.edCess      && <BRow label={bd.edCess.label}      rate={bd.edCess.rate}      value={bd.edCess.value}      currency={result.currency} indent />}
                    <BRow label="Gross Rate to Man" rate={null} value={bd.grossRateToMan.value} currency={result.currency} isSubtotal />

                    {bd.salarySplit?.map((s,i) => <BRow key={i} label={s.label} rate={s.rate} value={s.value} currency={result.currency} indent />)}

                    <tr style={{ background:'#f0f7ff' }}>
                      <td style={{ padding:'8px 16px', fontWeight:600, fontSize:13, color:'var(--text)' }}>In-Country Burdens</td>
                      <td></td>
                      <td style={{ padding:'8px 16px', textAlign:'right', fontWeight:600, fontSize:13, color:'var(--text)' }}>{result.currency} {fmt(bd.burdens.total)}</td>
                    </tr>
                    {bd.burdens.items.map((x,i)=>x.value!==0&&<BRow key={i} label={x.label} rate={x.rate} value={x.value} currency={result.currency} indent />)}

                    <BRow label="Gross Rate Post Employer Burdens" rate={null} value={bd.grossRatePostBurdens.value} currency={result.currency} isSubtotal />
                    {bd.vmsFee && <BRow label="VMS Fee" rate={bd.vmsFee.rate} value={bd.vmsFee.value} currency={result.currency} indent />}
                    <BRow label="Invoice Value Pre Tax" rate={null} value={bd.invoicePreTax.value} currency={result.currency} isSubtotal />
                    {bd.tax && bd.tax.value !== 0 && <BRow label={bd.tax.label} rate={bd.tax.rate} value={bd.tax.value} currency={result.currency} indent />}
                    <BRow label="Invoice Value Post Tax" rate={null} value={bd.invoicePostTax.value} currency={result.currency} isTotal />
                  </tbody>
                </table>
              </div>

              {/* Multiplier footer */}
              <div style={{ padding:'14px 20px', background:'var(--surface2)', borderTop:'1px solid var(--border)', display:'flex', gap:28, flexWrap:'wrap' }}>
                {[
                  ['Multiplier (Pre-Tax)',  `${result.multiplierPreTax}x`,  'var(--primary)'],
                  ['Multiplier (Post-Tax)', `${result.multiplierPostTax}x`, 'var(--accent)'],
                  [result.rateType === 'net' ? `Gross Rate / ${(result.billingFrequency||'Per Day').replace('Per ','')}` : `Bill Rate / ${(result.billingFrequency||'Per Day').replace('Per ','')}`, `${result.currency} ${fmt(result.billRateDisplay ?? result.billRatePerDay)}`, 'var(--text)'],
                ].map(([lbl,val,color]) => (
                  <div key={lbl}>
                    <div style={{ fontSize:11, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'.05em' }}>{lbl}</div>
                    <div style={{ fontSize:17, fontWeight:700, color }}>{val}</div>
                  </div>
                ))}
              </div>


            </div>
          </>}
        </div>

      {/* ── Quota / Feature / Country blocking modal ─────────────────── */}
      {blockModal && (
        <div style={{
          position:'fixed',inset:0,background:'rgba(0,0,0,.5)',
          display:'flex',alignItems:'center',justifyContent:'center',
          zIndex:1000,padding:20,backdropFilter:'blur(2px)',
        }}>
          <div style={{
            background:'var(--surface)',borderRadius:16,
            width:'100%',maxWidth:440,
            boxShadow:'0 24px 64px rgba(0,0,0,.2)',
            border:'1px solid var(--border)',
            overflow:'hidden',
          }}>
            {/* Header */}
            <div style={{
              background: blockModal.type === 'quota' ? '#0C1220' : blockModal.type === 'country' ? '#1e3a5f' : '#4c1d95',
              padding:'20px 24px',
              display:'flex',alignItems:'center',gap:12,
            }}>
              <div style={{
                width:40,height:40,borderRadius:10,flexShrink:0,
                background:'rgba(255,255,255,.12)',
                display:'flex',alignItems:'center',justifyContent:'center',
              }}>
                <i className={`ti ti-${blockModal.type === 'quota' ? 'chart-bar' : blockModal.type === 'country' ? 'world-off' : 'lock'}`}
                  style={{fontSize:20,color:'#38BDF8'}} aria-hidden="true"/>
              </div>
              <div>
                <div style={{color:'#fff',fontWeight:700,fontSize:15}}>{blockModal.title}</div>
                <div style={{color:'rgba(255,255,255,.5)',fontSize:12,marginTop:2}}>
                  {blockModal.type === 'quota' ? 'Action required to continue' : 'Plan restriction'}
                </div>
              </div>
            </div>

            {/* Body */}
            <div style={{padding:'20px 24px'}}>

              {/* Quota exceeded */}
              {blockModal.type === 'quota' && (
                <>
                  <div style={{
                    background:'var(--surface-1)',border:'1px solid var(--border)',
                    borderRadius:10,padding:'14px 16px',marginBottom:16,
                  }}>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,textAlign:'center'}}>
                      {[
                        { label:'Used', value: blockModal.quota_used, color:'var(--danger)' },
                        { label:'Limit', value: blockModal.quota_limit, color:'var(--text)' },
                        { label:'Credits', value: blockModal.credit_balance || 0, color:'var(--success)' },
                      ].map(s => (
                        <div key={s.label}>
                          <div style={{fontSize:22,fontWeight:700,color:s.color}}>{s.value}</div>
                          <div style={{fontSize:11,color:'var(--muted)',marginTop:2}}>{s.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <p style={{fontSize:13.5,color:'var(--text2)',lineHeight:1.65,marginBottom:18}}>
                    You have used all <strong style={{color:'var(--text)'}}>{blockModal.quota_limit}</strong> quotations included in your plan this month.
                    Purchase a top-up to continue immediately, or upgrade your plan for a higher monthly quota.
                  </p>
                  <div style={{display:'flex',gap:10}}>
                    <button className="btn btn-primary" style={{flex:1,justifyContent:'center'}}
                      onClick={() => { setBlockModal(null); navigate('/subscription'); }}>
                      <i className="ti ti-bolt" aria-hidden="true"/> Buy top-up
                    </button>
                    <button className="btn btn-outline" style={{flex:1,justifyContent:'center'}}
                      onClick={() => { setBlockModal(null); navigate('/plans'); }}>
                      <i className="ti ti-arrow-up-circle" aria-hidden="true"/> View plans
                    </button>
                  </div>
                </>
              )}

              {/* Feature not available */}
              {blockModal.type === 'feature' && (
                <>
                  <p style={{fontSize:13.5,color:'var(--text2)',lineHeight:1.65,marginBottom:18}}>
                    {blockModal.message} Your current plan is <strong style={{color:'var(--text)',textTransform:'capitalize'}}>{blockModal.current_plan}</strong>.
                    Upgrade to <strong style={{color:'var(--precision)'}}>Standard</strong> or above to unlock this feature.
                  </p>
                  <button className="btn btn-primary" style={{width:'100%',justifyContent:'center'}}
                    onClick={() => { setBlockModal(null); navigate('/plans'); }}>
                    <i className="ti ti-arrow-up-circle" aria-hidden="true"/> View upgrade options
                  </button>
                </>
              )}

              {/* Country not allowed */}
              {blockModal.type === 'country' && (
                <>
                  <p style={{fontSize:13.5,color:'var(--text2)',lineHeight:1.65,marginBottom:12}}>
                    <strong style={{color:'var(--text)'}}>{country}</strong> is not included in your current plan.
                    Your active countries are:
                  </p>
                  <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:18}}>
                    {(blockModal.allowed_countries || []).map(c => (
                      <span key={c} style={{
                        background:'var(--cloud)',color:'var(--precision-h)',
                        border:'1px solid var(--sky)',padding:'4px 12px',
                        borderRadius:20,fontSize:12.5,fontWeight:500,
                      }}>{c}</span>
                    ))}
                  </div>
                  <p style={{fontSize:12.5,color:'var(--muted)',marginBottom:18,lineHeight:1.6}}>
                    Upgrade to Standard to unlock all 8 countries, or contact your administrator to update your country selection.
                  </p>
                  <button className="btn btn-primary" style={{width:'100%',justifyContent:'center'}}
                    onClick={() => { setBlockModal(null); navigate('/plans'); }}>
                    <i className="ti ti-arrow-up-circle" aria-hidden="true"/> View upgrade options
                  </button>
                </>
              )}

              <button className="btn btn-ghost" style={{width:'100%',justifyContent:'center',marginTop:10}}
                onClick={() => setBlockModal(null)}>
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}